import React, { useState } from "react";
import { toast } from "react-toastify";
import { IoClose } from "react-icons/io5";

/**
 * "Add promotion code" control for the checkout summary.
 *
 * Applying a code cannot re-price the invoice the Payment Element is already
 * mounted against, so the backend rebuilds the subscription and hands back a
 * new client secret. `onApplied` receives that payload; the wrapper re-mounts
 * <Elements> with it.
 */
const PromoCodeField = ({
  planKey,
  checkoutToken,
  promo,
  onApplied,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [code, setCode] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const submit = async (value) => {
    if (isBusy) return;
    setIsBusy(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}api/stripe/apply-promotion-code`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // The checkout token is the whole identity of this request: the server
        // reads the customer off the PaymentIntent it names. Nothing about the
        // signed-in browser is sent, so this works from the desktop app's
        // browser too.
        body: JSON.stringify({ code: value, plan: planKey, checkoutToken }),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {
        // ignore non-json
      }

      if (!res.ok || !payload?.success) {
        toast.error(
          payload?.message || payload?.error || "Could not apply that code."
        );
        return;
      }

      onApplied(payload);
      setCode("");
      setIsOpen(false);
    } catch (err) {
      toast.error(err?.message || "Network error. Please try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const textSize = compact ? "text-[13px]" : "text-[15px]";

  // Applied — show the code with a way to take it back off
  if (promo?.promoCode) {
    return (
      <div className="flex items-center justify-between gap-3 font-inter">
        <span
          className={`inline-flex items-center gap-2 rounded-md bg-[#EBF7FE] px-2.5 py-1 font-medium uppercase tracking-wide text-[#3AB1F6] ${
            compact ? "text-[11px]" : "text-[12px]"
          }`}
        >
          {promo.promoCode}
        </span>

        <button
          type="button"
          onClick={() => submit("")}
          disabled={isBusy}
          className={`flex items-center gap-1 text-[#1A1A1A80] hover:text-[#1A1A1A] disabled:opacity-60 ${
            compact ? "text-[12px]" : "text-[13px]"
          }`}
        >
          <IoClose className="size-4" />
          {isBusy ? "Removing…" : "Remove"}
        </button>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`w-max font-inter font-medium text-[#3AB1F6] hover:underline ${textSize}`}
      >
        Add promotion code
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim()) submit(code.trim());
      }}
      className="flex items-center gap-2 font-inter"
    >
      <input
        type="text"
        value={code}
        autoFocus
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Promotion code"
        className={`flex-1 min-w-0 rounded-lg border border-[#00000020] bg-white px-3 py-2 uppercase tracking-wide text-[#1A1A1AE5] outline-none focus:border-[#3AB1F6] ${textSize}`}
      />

      <button
        type="submit"
        disabled={isBusy || !code.trim()}
        className={`shrink-0 rounded-lg bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-4 py-2 font-semibold text-white disabled:opacity-60 ${textSize}`}
      >
        {isBusy ? "Applying…" : "Apply"}
      </button>
    </form>
  );
};

export default PromoCodeField;
